import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from '../../../../components/ui/Dialog';
import { MatchStatus, resolveEnum } from '@/models/tournament';
import type { MatchView } from '@/models/tournament';

interface ChangeStatusModalProps {
  isOpen: boolean;
  match: MatchView | null;
  newStatus: MatchStatus | null;
  onClose: () => void;
  onConfirm: (matchId: number, newStatus: MatchStatus, reason: string | undefined) => Promise<void> | void;
}

const STATUS_LABEL_KEYS: Record<MatchStatus, string> = {
  [MatchStatus.Scheduled]: 'Scheduled',
  [MatchStatus.InProgress]: 'In Progress',
  [MatchStatus.Completed]: 'Completed',
  [MatchStatus.Closed]: 'Closed',
  [MatchStatus.UnderRevision]: 'Under Revision',
  [MatchStatus.Cancelled]: 'Cancelled',
};

/**
 * Confirms an HR status change from the match table dropdown. The server's transition table
 * (`allowedMatchStatusTargets`) decides what the dropdown offers; this modal collects the reason —
 * required for a cancellation (points and any generated payroll are voided), optional otherwise.
 */
const ChangeStatusModal: React.FC<ChangeStatusModalProps> = ({ isOpen, match, newStatus, onClose, onConfirm }) => {
  const { t } = useTranslation('tournament');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setReason('');
      setError(null);
      setIsSubmitting(false);
    }
  }, [isOpen, match?.id, newStatus]);

  if (!isOpen || !match || newStatus == null) return null;

  const currentStatus = (resolveEnum(MatchStatus, match.status) as MatchStatus) ?? MatchStatus.Scheduled;
  const isCancel = newStatus === MatchStatus.Cancelled;
  const isRestore = currentStatus === MatchStatus.Cancelled && newStatus === MatchStatus.Scheduled;
  const isReopen = currentStatus === MatchStatus.Closed && newStatus === MatchStatus.UnderRevision;
  const hasPaidAssignments = (match.refereeTeamMembers ?? []).some((tm) => tm.payrollVariablePayId != null);

  const handleConfirm = async () => {
    if (isCancel && !reason.trim()) {
      setError(t('A reason is required to cancel a match'));
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      await onConfirm(match.id, newStatus, reason.trim() || undefined);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    // Dismissal is locked while the confirmation is in flight, exactly like the Cancel button —
    // X, Escape and backdrop all route through onOpenChange.
    <Dialog open onOpenChange={(open) => { if (!open && !isSubmitting) onClose(); }}>
      <DialogContent size="sm" data-testid="change-status-modal">
        <DialogHeader className="py-4">
          <div className="flex items-center">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center me-3 flex-shrink-0 ${isCancel ? 'bg-red-100' : 'bg-primary/10'}`}>
              <Icon name={isCancel ? 'XCircle' : 'RefreshCcw'} size={20} className={isCancel ? 'text-red-600' : 'text-primary'} />
            </div>
            <div>
              <DialogTitle className="font-bold">
                {isCancel ? t('Cancel Match') : isRestore ? t('Restore Match') : isReopen ? t('Reopen Match') : t('Change Status')}
              </DialogTitle>
              <DialogDescription>
                {match.team1Name} {t('vs')} {match.team2Name}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <DialogBody className="space-y-4">
          <p className="text-sm text-foreground" data-testid="change-status-transition">
            {t('statusTransition', {
              from: t(STATUS_LABEL_KEYS[currentStatus]),
              to: t(STATUS_LABEL_KEYS[newStatus]),
            })}
          </p>

          {isCancel && (
            <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-lg p-3 text-sm text-red-800 dark:text-red-300">
              <p>{t('Cancelling voids the work points of every assigned official.')}</p>
              {hasPaidAssignments && (
                <p className="mt-1 font-medium">{t('This match is already on a payroll entry. If saving takes back pay, you review and approve the deduction first.')}</p>
              )}
            </div>
          )}

          <div>
            <label htmlFor="change-status-reason" className="block text-sm font-medium text-foreground mb-1">
              {t('Reason')}
              {isCancel ? <span className="text-red-500 ms-1">*</span> : <span className="text-muted-foreground ms-1 text-xs">({t('optional')})</span>}
            </label>
            <textarea
              id="change-status-reason"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              aria-required={isCancel || undefined}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? 'change-status-error' : undefined}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
              placeholder={isCancel ? t('Why is this match cancelled?') : t('Add a note for the audit trail')}
              data-testid="change-status-reason"
            />
            {error && <p id="change-status-error" className="mt-1 text-sm text-red-600" role="alert" data-testid="change-status-error">{error}</p>}
          </div>
        </DialogBody>

        <DialogFooter className="py-4 gap-2">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting} data-testid="change-status-cancel-btn">
            {t('Keep current status')}
          </Button>
          <Button
            variant={isCancel ? 'destructive' : 'default'}
            onClick={handleConfirm}
            disabled={isSubmitting}
            data-testid="change-status-confirm-btn"
          >
            {isSubmitting ? t('Saving...') : t('Confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ChangeStatusModal;
